import React from "react"
import { Formik } from "formik"
import * as Yup from "yup"
import axios from "axios"
import PropTypes from "prop-types"
import { useSnackbar } from "react-simple-snackbar"

import {
  ButtonWithLoader,
  Form,
  FormPage,
  FormSection,
  Input,
  Label,
  Modal,
  PhotoUploader,
  SnackBarOptions,
  TextArea,
  Seo,
} from "../../../../Shared"
import { defaultRequiredText } from "../../../../../constants"
import { handleBackendErrors } from "../../../../../utilities"
import { BoilerPhotoExamplesModal } from "../../../BoilerPhotoExamplesModal"

import MoreInfoNeededIllustration from "../../../../../images/illustrations/more-info-needed.svg"

export const MoreInfoNeededForm = ({ quoteId, setCallbackInfo, location }) => {
  const [openSnackbar] = useSnackbar(SnackBarOptions)
  const [photoExamplesVisible, setPhotoExamplesVisible] = React.useState(false)
  const [photos, setPhotos] = React.useState([])

  const onSubmit = async (values, { setSubmitting }) => {
    let formData = new FormData()
    formData.append("quoteId", quoteId)
    formData.append("quoteCallbackRequest", JSON.stringify(values))
    photos.forEach((photo) => {
      formData.append("files", photo.file)
    })

    try {
      const response = await axios({
        method: "post",
        url: `${process.env.GATSBY_API_BASE_URL}/quote/callback-request/create`,
        data: formData,
      })
      setSubmitting(false)
      setCallbackInfo(response.data.quoteCallbackRequest)
    } catch (error) {
      openSnackbar(handleBackendErrors(error, "save callback request"))
    }
  }

  return (
    <FormPage>
      <Seo
        url={location && location.href ? location.href : null}
        title="More Information Needed | Home Assist Repairs and Servicing"
      />
      <header className="form-page-header more-info-needed-header">
        <MoreInfoNeededIllustration />
        <h1 className="heading-large">More information needed</h1>
        <p>
          To make sure your quote is as accurate as possible we need to ask you
          a couple more questions over the phone.
        </p>
        <p>We’ll ring you within 1-2 working days to discuss your property.</p>
      </header>
      <section>
        <Formik
          initialValues={{
            notes: "",
            name: "",
            email: "",
            mobile: "",
            homePhone: "",
            goodTimeToCall: "",
          }}
          validationSchema={Yup.object().shape({
            notes: Yup.string(),
            name: Yup.string().required(defaultRequiredText),
            email: Yup.string()
              .email("Must be a valid email address")
              .required(defaultRequiredText),
            mobile: Yup.string().required(defaultRequiredText),
            homePhone: Yup.string(),
            goodTimeToCall: Yup.string(),
          })}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="form">
                <FormSection>
                  <PhotoUploader photos={photos} updatePhotos={setPhotos}>
                    <Label name="photos" optional={true}>
                      Photos of your current boiler{" "}
                      <button
                        onClick={() => setPhotoExamplesVisible(true)}
                        type="button"
                        className="link-button"
                      >
                        View examples
                      </button>
                      <BoilerPhotoExamplesModal
                        dismissFunc={() => setPhotoExamplesVisible(false)}
                        isVisible={photoExamplesVisible}
                      />
                    </Label>
                  </PhotoUploader>
                  <TextArea
                    name="notes"
                    label="Additional notes"
                    optional={true}
                  />
                </FormSection>
                <FormSection>
                  <Input name="name" label="Your name" />
                  <Input name="email" label="Email" type="phone" />
                  <Input name="mobile" label="Mobile" type="phone" />
                  <Input name="homePhone" label="Home phone" optional={true} />
                  <TextArea
                    name="goodTimeToCall"
                    label="Is there a good time for us to call?"
                    optional={true}
                  />
                </FormSection>
              </div>
              <ButtonWithLoader
                additionalClasses="button-submit"
                disabled={isSubmitting}
                type="submit"
              >
                Request callback
              </ButtonWithLoader>
            </Form>
          )}
        </Formik>
      </section>
    </FormPage>
  )
}

MoreInfoNeededForm.propTypes = {
  quoteId: PropTypes.string.isRequired,
  setCallbackInfo: PropTypes.func.isRequired,
}
